import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Phone, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const Auth = () => {
  const { t, dir } = useLanguage();
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usePhone, setUsePhone] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    preferred_language: 'fr',
  });

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        const identifier = usePhone ? formData.phone : formData.email;
        await login(identifier, formData.password);
        toast.success('Connexion réussie');
        navigate('/dashboard');
      } else {
        await register({
          ...formData,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
        });
        toast.success('Compte créé avec succès');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-light flex" dir={dir}>
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1761110787206-2cc164e4913c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBldmVudCUyMGdhbGElMjB3ZWRkaW5nJTIwZWxlZ2FudHxlbnwwfHx8fDE3NzQ0NTY1MTB8MA&ixlib=rb-4.1.0&q=85"
          alt="Elegant event"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-dark/80 to-transparent" />
        <div className="absolute bottom-16 left-16 max-w-md">
          <h2 className="font-heading text-4xl text-white font-light tracking-tight mb-4">
            Nûr Events
          </h2>
          <p className="font-body text-white/70">
            L'Excellence Événementielle en Afrique Centrale
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-8">
            <span className="font-heading text-2xl font-light tracking-tight text-text-primary">
              Nûr <span className="text-primary">Events</span>
            </span>
          </Link>

          <h1 className="font-heading text-3xl font-light text-text-primary mb-2" data-testid="auth-title">
            {mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
          </h1>
          <p className="font-body text-text-muted mb-8">
            {mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-primary hover:underline"
              data-testid="auth-switch-mode"
            >
              {mode === 'login' ? t('auth.registerBtn') : t('auth.loginBtn')}
            </button>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {mode === 'register' && (
              <div className="relative">
                <User className="absolute left-0 top-3 w-5 h-5 text-text-muted" />
                <input
                  type="text"
                  placeholder={t('auth.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-flushed w-full pl-8"
                  data-testid="auth-name"
                />
              </div>
            )}

            {mode === 'login' && (
              <div className="flex items-center gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setUsePhone(false)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-body transition-colors ${
                    !usePhone ? 'text-primary border-b-2 border-primary' : 'text-text-muted'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setUsePhone(true)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-body transition-colors ${
                    usePhone ? 'text-primary border-b-2 border-primary' : 'text-text-muted'
                  }`}
                >
                  <Phone className="w-4 h-4" />
                  {t('auth.phone')}
                </button>
              </div>
            )}

            {mode === 'register' ? (
              <>
                <div className="relative">
                  <Mail className="absolute left-0 top-3 w-5 h-5 text-text-muted" />
                  <input
                    type="email"
                    placeholder={t('auth.email')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-flushed w-full pl-8"
                    data-testid="auth-email"
                  />
                </div>
                <div className="relative">
                  <Phone className="absolute left-0 top-3 w-5 h-5 text-text-muted" />
                  <input
                    type="tel"
                    placeholder={t('auth.phone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-flushed w-full pl-8"
                    data-testid="auth-phone"
                  />
                </div>
              </>
            ) : (
              <div className="relative">
                {usePhone ? (
                  <Phone className="absolute left-0 top-3 w-5 h-5 text-text-muted" />
                ) : (
                  <Mail className="absolute left-0 top-3 w-5 h-5 text-text-muted" />
                )}
                <input
                  type={usePhone ? 'tel' : 'email'}
                  placeholder={usePhone ? t('auth.phone') : t('auth.email')}
                  value={usePhone ? formData.phone : formData.email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [usePhone ? 'phone' : 'email']: e.target.value,
                    })
                  }
                  required
                  className="input-flushed w-full pl-8"
                  data-testid="auth-identifier"
                />
              </div>
            )}

            <div className="relative">
              <Lock className="absolute left-0 top-3 w-5 h-5 text-text-muted" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.password')}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="input-flushed w-full pl-8 pr-10"
                data-testid="auth-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-3 text-text-muted hover:text-text-primary"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="auth-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
              ) : mode === 'login' ? (
                t('auth.loginBtn')
              ) : (
                t('auth.registerBtn')
              )}
            </button>
          </form>

          {/* Admin login hint */}
          <div className="mt-8 p-4 bg-surface-light border border-primary/10">
            <p className="font-body text-xs text-text-muted">
              <strong>Admin:</strong> admin@nurevents.com / Admin123!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
